import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { showGeneralErrorMessage } from '../../../constants/UI';
import { useRegionContext } from '../../../context/Region';
import { useDropZone } from '../../../hooks/useDropZone/useDropZone';
import { useGetSplitPolygonDataMutation } from '../../../redux/api/biomassApi';
import {
  useBatchAddRegionsMutation,
  useExtractPolygonsFromFileMutation,
} from '../../../redux/api/regionApi';
import { closeModal } from '../../../redux/features/modal/modal-slice';
import {
  setShowSavedRegions,
  setShowSidebar,
} from '../../../redux/features/ui/ui-slice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { IRegionRequest } from '../../../types/API/Region';
import { ISplitPolygonState } from '../../../types/Geo';
import { checkRegionSize } from '../../../utils/helpers';
import Button from '../../Common/Button';
import InformationBanner from '../../Common/InformationBanner';
import Modal from '../../Common/Modal';
import Spacer from '../../Common/Spacer';
import EditRegion from './EditRegion';
import {
  FooterButtonsWrapper,
  UploadInformation,
  UploadItemsList,
  UploadModalSx,
} from './style';
import UploadItemComponent from './UploadItem';

export interface IItem {
  data: GeoJSON.Feature<GeoJSON.Polygon, GeoJSON.GeoJsonProperties>;
  name: string;
  filename: string;
  hasMissingData?: boolean;
}

export const SUPPORTED_FILETYPES = '.geojson,.shp,.kml,.json';

const UploadModal = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [batchAddRegions, { isLoading, isError, reset: resetBatch }] =
    useBatchAddRegionsMutation();
  const [extractPolygonsFromFile, { data, isSuccess: isExtracted, reset }] =
    useExtractPolygonsFromFileMutation();

  const { user } = useAppSelector((state) => state.userState);
  const [verifySplitPolygon] = useGetSplitPolygonDataMutation();

  const { reportRegion } = useRegionContext();

  const [errorText, setErrorText] = useState('');
  const [reportMissingData, setReportMissingData] = useState(false);
  const [polygonMessage, setPolygonMessage] = useState('');

  const [regionsToSave, setRegionsToSave] = useState<IItem[]>([]);
  const [polygonsWithoutDataParts, setPolygonsWithoutDataParts] = useState<
    IItem[]
  >([]);
  const [polygonsWithDataParts, setPolygonsWithDataParts] = useState<IItem[]>(
    []
  );

  const [regionToEdit, setRegionToEdit] = useState<IItem | null>(null);
  const [hasMissingData, setHasMissingData] = useState(false);

  const regionsWithNameLength = useMemo(
    () => regionsToSave.filter((el) => el.name === '').length,
    [regionsToSave]
  );
  const couldSave = useMemo(() => {
    return (
      (regionsWithNameLength === 0 &&
        polygonsWithoutDataParts.length === 0 &&
        polygonsWithDataParts.length > 0 &&
        !reportMissingData) ||
      (polygonsWithoutDataParts.length > 0 &&
        reportMissingData &&
        polygonMessage.length > 0)
    );
  }, [
    polygonMessage.length,
    polygonsWithDataParts.length,
    polygonsWithoutDataParts.length,
    regionsWithNameLength,
    reportMissingData,
  ]);

  const handleToggleReportMissingData = useCallback(() => {
    setReportMissingData((wasSent) => !wasSent);
  }, []);

  const createElementsToRender = useCallback(
    (polygons: GeoJSON.Feature<GeoJSON.Polygon>[], name: string): IItem[] =>
      polygons.map((polygon) => {
        return {
          data: polygon,
          name: '',
          filename: name,
        };
      }),
    []
  );

  /**
   * Check if there is any data in the region with the use of the split polygon function
   * @returns {Promise<{regionsWithData: IItem[], regionsWithoutDataParts: IItem[], splitPolygonDataParts: ISplitPolygonState[]}>}
   */
  const checkRegionsData = useCallback(async () => {
    const regionsWithData: IItem[] = [];
    const regionsWithoutDataParts: IItem[] = [];
    const splitPolygonDataParts: ISplitPolygonState[] = [];
    let missingDataAppeared = false;
    const entirelyWithoutDataParts: IItem[] = [];

    await Promise.all(
      regionsToSave.map(async (region) => {
        const splitData = await verifySplitPolygon({
          feature: region.data,
          omitCache: true,
          withStateSet: false,
        }).unwrap();

        if (splitData.drawnDataOnTile.features.length > 0) {
          regionsWithData.push(region);
          splitPolygonDataParts.push(splitData);
        }

        if (splitData.drawnDataOutsideTile.features.length > 0) {
          regionsWithoutDataParts.push(region);
          missingDataAppeared = true;
        }

        if (
          splitData.drawnDataOnTile.features.length === 0 &&
          missingDataAppeared
        ) {
          entirelyWithoutDataParts.push(region);
        }

        // clean the message if there is no missing data
        if (!missingDataAppeared) {
          setPolygonMessage('');
          setReportMissingData(false);
        }
        // set the flag if there is any missing data
        setHasMissingData(missingDataAppeared);
      })
    );

    if (entirelyWithoutDataParts.length > 0) {
      setPolygonsWithoutDataParts(entirelyWithoutDataParts);
    }

    if (regionsWithData.length > 0) {
      setPolygonsWithDataParts(regionsWithData);
    }
    return {
      regionsWithData,
      regionsWithoutDataParts,
      splitPolygonDataParts,
      entirelyWithoutDataParts,
    };
  }, [regionsToSave, verifySplitPolygon]);

  /**
   * Check if there are any polygons with the same geometry in the list already
   * @param {IItem[]} polygons
   * @returns {IItem[] | null}
   */
  const filterOutPolygonsWithSameGeometry = useCallback(
    (polygons: IItem[]): IItem[] | null => {
      const newRegionsList = polygons.filter(
        (polygon) =>
          !regionsToSave.some(
            (el) =>
              JSON.stringify(el.data.geometry) ===
              JSON.stringify(polygon.data.geometry)
          )
      );

      if (newRegionsList.length === 0) {
        toast.error(
          t('Regions from this file are already added to list', {
            toastId: 'upload-regions-already-on-list',
            autoClose: 3000,
          })
        );
        return null;
      }

      return newRegionsList;
    },
    [regionsToSave, t]
  );

  const uploadFileAndPrepareData = useCallback((files: FileList) => {
    const form = new FormData();
    form.append('file', files[0]);
    return form;
  }, []);

  const { renderUploadSection, filename, inputRef, formData, setFormData } =
    useDropZone({
      prepareData: uploadFileAndPrepareData,
      accept: SUPPORTED_FILETYPES,
    });

  useEffect(() => {
    if (formData && !regionToEdit) {
      void extractPolygonsFromFile(formData);
    }
  }, [extractPolygonsFromFile, formData, regionToEdit]);

  useEffect(() => {
    if (isExtracted && data) {
      const polygonsToSave = createElementsToRender(data.polygons, filename);
      const newRegionsList = filterOutPolygonsWithSameGeometry(polygonsToSave);

      if (newRegionsList) {
        const polygonsLessThanMaxSize = newRegionsList.filter((polygon) =>
          checkRegionSize(polygon.data, user?.role || 'free')
        );
        if (polygonsLessThanMaxSize.length === 0) {
          toast.error(
            t('All Regions from this file are too big to be added', {
              toastId: 'upload-regions-all-too-big',
              autoClose: 3000,
            })
          );
          return;
        }
        if (polygonsLessThanMaxSize.length < newRegionsList.length) {
          toast.warning(
            t('Some Regions from this file are too big to be added', {
              toastId: 'upload-regions-too-big',
              autoClose: 3000,
            })
          );
        }
        setRegionsToSave((prevRegions) => [
          ...prevRegions,
          ...polygonsLessThanMaxSize,
        ]);
      }

      if (inputRef.current) inputRef.current.value = '';
      setFormData(null);
      reset();
    }
  }, [
    createElementsToRender,
    data,
    filename,
    filterOutPolygonsWithSameGeometry,
    inputRef,
    isExtracted,
    reset,
    setFormData,
    t,
    user?.role,
  ]);

  // Re verifying data when the regionsToSave list changes,
  // This brings the possibility to report missing data parts if those occur on uploaded regions
  useEffect(() => {
    if (regionsToSave.length > 0) {
      void checkRegionsData();
    }
  }, [regionsToSave, checkRegionsData]);

  const handleSelectRegionToEdit = useCallback((region: IItem) => {
    setRegionToEdit(region);
  }, []);

  const onRemovePolygon = useCallback(
    (item: IItem) => {
      // remove from polygonsWithoutDataParts if it's there
      const newPolygonsWithoutDataParts = polygonsWithoutDataParts.filter(
        (el) => JSON.stringify(el) !== JSON.stringify(item)
      );
      setPolygonsWithoutDataParts(newPolygonsWithoutDataParts);

      // do the same with polygonsWithDataParts
      const newPolygonsWithDataParts = polygonsWithDataParts.filter(
        (el) => JSON.stringify(el) !== JSON.stringify(item)
      );
      setPolygonsWithDataParts(newPolygonsWithDataParts);
      const newRegionsList = regionsToSave.filter(
        (el) => JSON.stringify(el) !== JSON.stringify(item)
      );
      setRegionsToSave(newRegionsList);
    },
    [polygonsWithDataParts, polygonsWithoutDataParts, regionsToSave]
  );

  const editUploadedRegionName = useCallback((name: string) => {
    if (name.length < 25) setErrorText('');
    setRegionToEdit((prev) =>
      prev
        ? {
            ...prev,
            name,
          }
        : null
    );
  }, []);

  const handleSaveRegionName = useCallback(() => {
    if (regionToEdit && regionToEdit.name.length > 25) {
      setErrorText(String(t('Region name is too long')));
      return;
    }

    const updatedRegions = regionsToSave.map((region) => {
      if (
        regionToEdit &&
        JSON.stringify(region.data.geometry) ===
          JSON.stringify(regionToEdit.data.geometry)
      ) {
        return regionToEdit;
      }
      return region;
    });
    setRegionsToSave(updatedRegions);
    setRegionToEdit(null);
  }, [regionToEdit, regionsToSave, t]);

  /**
   * Send a request to the backend to request adding missing data parts for the regions
   * @param {IItem[]} regionsWithoutDataParts
   */
  const handleReportMissingPolygonParts = useCallback(
    async (regionsWithoutDataParts: IItem[]) => {
      if (reportMissingData && user?.email) {
        if (polygonMessage.length === 0) {
          return false;
        }

        const polygonCollection: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: regionsWithoutDataParts.map((region) => region.data),
        };

        await reportRegion(polygonCollection, user.email);
        return true;
      }
    },
    [polygonMessage.length, reportRegion, reportMissingData, user?.email]
  );

  /**
   * Prepare data to be sent to the backend, with the dataUpdatedAt field, to be used in the backend
   * @param {IItem[]} regionsWithData
   * @param {ISplitPolygonState[]} splitPolygonDataParts
   * @returns {IRegionRequest[]}
   */
  const prepareSubmitData = useCallback(
    (regionsWithData: IItem[], splitPolygonDataParts: ISplitPolygonState[]) => {
      const preparedData: IRegionRequest[] = regionsWithData.map(
        (el, index) => {
          let dataUpdatedAt: Date | undefined;
          const dates = splitPolygonDataParts[index].drawnDataOnTile.features
            .filter((splitData) => splitData.properties?.last_modified)
            .map(
              (splitDataEl) =>
                new Date(String(splitDataEl.properties?.last_modified))
            );

          if (dates.length > 0) {
            dataUpdatedAt = dates.reduce((a, b) => (a > b ? a : b));
          }

          return {
            name: el.name,
            polygon: el.data,
            dataUpdatedAt,
          };
        }
      );

      return preparedData;
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    const {
      regionsWithData,
      splitPolygonDataParts,
      regionsWithoutDataParts,
      entirelyWithoutDataParts,
    } = await checkRegionsData();

    if (regionsWithoutDataParts.length > 0) {
      await handleReportMissingPolygonParts(regionsWithoutDataParts);
    }

    if (regionsWithData.length > 0 && entirelyWithoutDataParts.length === 0) {
      const preparedData = prepareSubmitData(
        regionsWithData,
        splitPolygonDataParts
      );

      const response = await batchAddRegions(preparedData);
      if (!('error' in response)) {
        toast.success(t('Regions saved successfully'), {
          autoClose: 3000,
          toastId: 'upload-regions-toast',
        });
        resetBatch();
        dispatch(closeModal());
        dispatch(setShowSavedRegions(true));
        dispatch(setShowSidebar(true));
      }
    } else {
      if (reportMissingData) {
        toast.info(
          t(
            'We are processing your reported regions, but we were not able to save it to your account right now. Please try again later',
            {
              toastId: 'upload-regions-processing-reported-toast',
              autoClose: 3000,
            }
          )
        );
      }
      dispatch(closeModal());
    }
  }, [
    checkRegionsData,
    handleReportMissingPolygonParts,
    prepareSubmitData,
    batchAddRegions,
    t,
    resetBatch,
    dispatch,
    reportMissingData,
  ]);

  useEffect(() => {
    if (isError) {
      showGeneralErrorMessage();
    }
  }, [isError]);

  return (
    <>
      <Modal
        additionalSx={UploadModalSx}
        withCloseIcon={false}
        dataTestId="upload-modal"
        modalType="uploadShapefile"
      >
        {regionToEdit ? (
          <EditRegion
            value={regionToEdit}
            onChange={editUploadedRegionName}
            onClick={handleSaveRegionName}
            disabled={regionToEdit.name.trim() === ''}
            errorText={errorText}
          />
        ) : (
          <>
            {renderUploadSection()}
            {regionsToSave.length > 0 ? (
              <UploadItemsList>
                {hasMissingData ? (
                  <InformationBanner
                    checked={reportMissingData}
                    onToggle={handleToggleReportMissingData}
                    text={t(
                      'At least one of your regions does not have data available on your current tier. Send a message to our team to request data for this region'
                    )}
                    message={polygonMessage}
                    setMessage={setPolygonMessage}
                  />
                ) : null}
                {!couldSave && polygonsWithoutDataParts.length === 0 ? (
                  <>
                    <Spacer size={'20px 0 0'} />
                    <UploadInformation data-test-id="upload-modal-upload-info">
                      {t('To save regions you must set its name.')}
                    </UploadInformation>
                  </>
                ) : null}
                {regionsToSave.map((region) => (
                  <UploadItemComponent
                    data-test-id="upload-modal-upload-item"
                    onClick={handleSelectRegionToEdit}
                    onRemove={onRemovePolygon}
                    region={region}
                    showRename={polygonsWithoutDataParts.length === 0}
                    isIncorrect={polygonsWithoutDataParts.some(
                      (el) =>
                        JSON.stringify(el.data.geometry) ===
                        JSON.stringify(region.data.geometry)
                    )}
                    key={JSON.stringify(region.data.geometry)}
                  />
                ))}
              </UploadItemsList>
            ) : null}
            <Spacer size={'15px 0'} />
            <FooterButtonsWrapper>
              <Button
                fullWidth
                variant="purple"
                disabled={!couldSave || isLoading}
                additionalSx={
                  regionsToSave.length > 0 ? { minWidth: '200px' } : {}
                }
                onClick={handleSubmit}
                dataTestId="upload-modal-save-regions-button"
              >
                {hasMissingData && polygonsWithoutDataParts.length > 0
                  ? t('Request missing data')
                  : t(
                      `Save new regions ${
                        polygonsWithDataParts.length > 0
                          ? `(${polygonsWithDataParts.length})`
                          : ''
                      }`
                    )}
              </Button>
              {regionsToSave.length > 0 ? (
                <Button
                  fullWidth
                  variant="red"
                  dataTestId="upload-modal-remove-all-regions-button"
                  onClick={() => {
                    setRegionsToSave([]);
                    setPolygonsWithoutDataParts([]);
                    setPolygonsWithDataParts([]);
                  }}
                  additionalSx={{
                    minWidth: '200px',
                  }}
                >
                  {t('Remove all regions')}
                </Button>
              ) : null}
            </FooterButtonsWrapper>
          </>
        )}
      </Modal>
    </>
  );
};

export default UploadModal;
